/**
 * /sponsor — post-purchase page where paid homepage sponsors upload their
 * logo, tagline, and URL. Gated by a valid sponsor_homepage_monthly token.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Loader2 } from "lucide-react";
import { verifyUnlockToken } from "@/lib/api";
import { getUnlockToken, clearUnlockToken } from "@/lib/aiUsage";
import SponsorForm from "@/components/SponsorForm";
import useSEO from "@/lib/useSEO";

export default function Sponsor() {
  useSEO({
    title: "Sponsor Dashboard · Centennial",
    description: "Manage your Centennial homepage sponsor entry. Requires an active sponsor token.",
    canonicalPath: "/sponsor",
  });
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [token, setToken] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);

  useEffect(() => {
    (async () => {
      const tok = getUnlockToken();
      if (!tok) {
        setChecking(false);
        return;
      }
      setToken(tok);
      try {
        const v = await verifyUnlockToken(tok);
        if (v?.valid && v?.package_id === "sponsor_homepage_monthly") {
          setAuthorized(true);
          setExpiresAt(v.expires_at);
        }
      } catch {
        setAuthorized(false);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const signOut = () => {
    clearUnlockToken();
    navigate("/");
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <Loader2 size={18} className="animate-spin text-[#5C6B62]" />
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] px-6 py-16 flex items-center justify-center">
        <div className="max-w-md w-full bg-white border border-[#E5E2DC] rounded-2xl p-8 text-center">
          <Lock size={20} className="mx-auto text-[#5C6B62] mb-3" />
          <h1 className="text-2xl font-medium text-[#1C2B23]">Sponsor slot required</h1>
          <p className="text-sm text-[#5C6B62] mt-2 leading-relaxed">
            A paid Homepage Sponsor Slot is required to access this page. $199/mo, one of 3
            rotating slots on the Centennial homepage &quot;Trusted by&quot; strip.
          </p>
          <Link
            to="/pricing"
            data-testid="sponsor-buy-cta"
            className="mt-6 inline-flex items-center justify-center bg-[#2B5339] text-white rounded-full px-6 py-2.5 text-sm hover:bg-[#1E3B28]"
          >
            See sponsor pricing
          </Link>
          <Link
            to="/"
            className="mt-4 block text-xs text-[#5C6B62] hover:text-[#2B5339]"
          >
            ← Back to calculator
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] px-6 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339]"
          >
            <ArrowLeft size={14} /> Back to calculator
          </Link>
          <button
            data-testid="sponsor-signout"
            onClick={signOut}
            className="text-xs text-[#5C6B62] hover:text-[#C26747]"
          >
            Sign out
          </button>
        </div>

        <h1 className="text-3xl font-medium text-[#1C2B23] mb-2">Sponsor dashboard</h1>
        <p className="text-sm text-[#5C6B62] mb-8">
          Set up how your brand appears on the Centennial homepage. Changes go live within seconds.
        </p>

        <SponsorForm token={token} expiresAt={expiresAt} />
      </div>
    </div>
  );
}
